import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend } from 'recharts';
import { returnsToCentered } from '../utils/returns';

/**
 * CumulativeReturnChart - running total of a playbook's returns, event by event
 *
 * Each event's return is centered at 0 ((raw - 1) * 100) and added to the total so far,
 * so the line shows total profit as a percentage of one event's budget
 * (the budget is the same every event, so returns add rather than compound).
 *
 * @param {Object} props
 * @param {Array<number>} props.returnsByEvent - Array of return values per event (multipliers)
 * @param {string} props.trainingDataCutoffDate - Date string for cutoff (events before this are training)
 * @param {Array<string>} props.eventDates - Array of event dates corresponding to returnsByEvent
 */
export default function CumulativeReturnChart({ returnsByEvent = [], trainingDataCutoffDate, eventDates }) {
  if (!returnsByEvent || returnsByEvent.length === 0) {
    return (
      <div className="chart-empty">
        No returns data available
      </div>
    );
  }

  // Helper function to parse date string without timezone issues
  const parseDate = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const formatSigned = (value) => `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;

  let total = 0;
  const data = returnsByEvent.map((returnValue, index) => {
    const eventDate = eventDates?.[index] || null;
    const isTraining = eventDate && trainingDataCutoffDate
      ? parseDate(eventDate) < parseDate(trainingDataCutoffDate)
      : false;
    const centered = returnsToCentered(returnValue);
    if (centered !== null) total += centered;
    const cumulative = parseFloat(total.toFixed(2));

    return {
      event: index + 1,
      date: eventDate,
      eventReturn: centered,
      cumulative,
      isTraining,
      trainingCumulative: isTraining ? cumulative : null,
      actualCumulative: !isTraining ? cumulative : null
    };
  });

  const useDates = eventDates && eventDates.length > 0 && trainingDataCutoffDate;
  const hasTrainingData = trainingDataCutoffDate && data.some(d => d.isTraining);
  const hasActualData = trainingDataCutoffDate && data.some(d => !d.isTraining);
  const cutoffEventIndex = useDates ? data.findIndex(d => d.date && parseDate(d.date) >= parseDate(trainingDataCutoffDate)) : -1;

  // Start the actual line at the last training point so the two lines join up
  if (hasTrainingData && hasActualData && cutoffEventIndex > 0) {
    data[cutoffEventIndex - 1].actualCumulative = data[cutoffEventIndex - 1].cumulative;
  }

  const finalTotal = data[data.length - 1].cumulative;
  const actualStart = cutoffEventIndex > 0 ? data[cutoffEventIndex - 1].cumulative : 0;
  const actualGain = hasTrainingData && hasActualData ? finalTotal - actualStart : null;

  const TrainingCutoffLabel = ({ viewBox }) => {
    if (!viewBox) return null;
    const labelY = Math.max(5, viewBox.y - 5);
    return (
      <g>
        <text
          x={viewBox.x}
          y={labelY}
          fill="#ECE9F3"
          fontSize={14}
          fontWeight={700}
          textAnchor="middle"
          style={{ pointerEvents: 'none' }}
        >
          Training cutoff
        </text>
      </g>
    );
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const point = payload[0].payload;
      const formatDate = (dateString) => {
        const date = parseDate(dateString);
        if (!date) return null;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      };

      return (
        <div className="chart-tooltip">
          <p className="chart-tooltip-title">
            {point.date ? formatDate(point.date) : `Event ${point.event}`}
          </p>
          <p>
            Total so far:{' '}
            <span className={point.cumulative >= 0 ? 'positive' : 'negative'}>{formatSigned(point.cumulative)}</span>
          </p>
          {point.eventReturn !== null && (
            <p>
              This event:{' '}
              <span className={point.eventReturn >= 0 ? 'positive' : 'negative'}>{formatSigned(point.eventReturn)}</span>
            </p>
          )}
          {trainingDataCutoffDate && (
            <p>{point.isTraining ? 'Training data' : 'Actual performance'}</p>
          )}
        </div>
      );
    }
    return null;
  };

  // Y axis on round steps (1, 2 or 5 times a power of ten), always including 0
  const allTotals = data.map(d => d.cumulative);
  const low = Math.min(...allTotals, 0);
  const high = Math.max(...allTotals, 0);
  const rawStep = Math.max((high - low) / 5, 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const step = [1, 2, 5, 10].map(m => m * magnitude).find(v => v >= rawStep);
  const minTotal = Math.floor(low / step) * step;
  const maxTotal = Math.ceil(high / step) * step;
  const yTicks = [];
  for (let v = minTotal; v <= maxTotal + step / 2; v += step) yTicks.push(Math.round(v));

  return (
    <div className="chart-block">
      <h3>Cumulative return</h3>
      <p className="chart-caption">
        Total profit so far, as a share of one event's budget: {formatSigned(finalTotal)} over {data.length} events
        {actualGain !== null && <>, {formatSigned(actualGain)} of it after the training cutoff</>}.
      </p>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ top: 40, right: 30, left: 0, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#2A2536" />
          <XAxis
            dataKey={useDates ? 'date' : 'event'}
            stroke="#6E6780"
            tick={{ fill: '#9A93AB', fontSize: 12 }}
            tickFormatter={useDates ? (value) => {
              const date = parseDate(value);
              if (!date) return '';
              return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            } : undefined}
            label={{
              value: useDates ? 'Date' : 'Event number',
              position: 'insideBottom',
              offset: -5,
              style: { fill: '#9A93AB', fontSize: 12 }
            }}
          />
          <YAxis
            stroke="#6E6780"
            tick={{ fill: '#9A93AB', fontSize: 12 }}
            domain={[minTotal, maxTotal]}
            ticks={yTicks}
            tickFormatter={(value) => `${value}%`}
            label={{
              value: 'Total return (%)',
              angle: -90,
              position: 'insideLeft',
              style: { fill: '#9A93AB', fontSize: 12 }
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          {hasTrainingData && hasActualData && (
            <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="line" />
          )}
          <ReferenceLine y={0} stroke="#6E6780" strokeWidth={2} strokeDasharray="4 4" />

          {hasTrainingData && (
            <Line
              type="linear"
              dataKey="trainingCumulative"
              stroke="#6E6780"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              activeDot={{ r: 4, fill: '#6E6780' }}
              name="Training data"
              connectNulls={false}
            />
          )}

          {hasActualData ? (
            <Line
              type="linear"
              dataKey="actualCumulative"
              stroke="#9B72E3"
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 5, fill: '#9B72E3' }}
              name="Actual performance"
              connectNulls={false}
            />
          ) : (
            <Line
              type="linear"
              dataKey="cumulative"
              stroke="#9B72E3"
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 5, fill: '#9B72E3' }}
              name="Cumulative return"
            />
          )}

          {cutoffEventIndex >= 0 && trainingDataCutoffDate && (
            <ReferenceLine
              x={useDates ? data[cutoffEventIndex]?.date : data[cutoffEventIndex]?.event}
              stroke="#ECE9F3"
              strokeWidth={3}
              strokeDasharray="5 5"
              strokeOpacity={1}
              label={<TrainingCutoffLabel />}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
